import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import type { PromotionReplyDto } from '@diritto/common/promotion/dtos/promotion-reply.dto';
import { Color } from '@diritto/common/style/color';
import { Typography } from '@diritto/common/style/typography';
import { useDeletePromotionReplyMutation } from '@/queries/promotions/mutations/useDeletePromotionReplyMutation';
import { usePromotionReplyLike } from '@/state-hooks/promotions/usePromotionReplyLike';
import { useAuthState } from 'contexts/AuthProvider';
import { FavoriteEmptyIcon, FavoriteFillIcon } from 'icons/FavoriteIcon';
import { IconButton } from 'ui-components/common/IconButton';
import { useRequestSign } from 'ui-hooks/useRequestSign';
import { ModalHandler } from '@/utils/ModalHandler';
import { ModalType } from '@/constants/modal';
import { showToast } from '@/utils/modal';

interface ReplyReactionProps {
  promotionId: number;
  reply: PromotionReplyDto;
  isLiked: boolean;
  showLike?: boolean;
  showDelete?: boolean;
  onDeletedSuccess?: () => void;
}

export const PromotionReplyReaction = ({
  promotionId,
  reply,
  isLiked,
  showLike = false,
  showDelete = false,
  onDeletedSuccess,
}: ReplyReactionProps) => {
  const { isLoggedIn, profile: myProfile } = useAuthState();
  const requestSign = useRequestSign();
  const isAuthor = !!(myProfile && reply.profileId === myProfile.id);
  const { liked, likeCount, toggleLike } = usePromotionReplyLike({
    promotionId,
    replyId: reply.id,
    isLiked,
    likeCount: reply.likeCount,
  });

  const { mutate: deleteMutate } = useDeletePromotionReplyMutation(promotionId, {
    onSuccess: () => {
      onDeletedSuccess?.();
      showToast('댓글이 삭제되었어요.');
    },
  });

  const handleDeleteConfirmModal = useCallback(() => {
    ModalHandler.show(ModalType.Alert, {
      title: '댓글을 삭제하시겠어요?',
      description: '댓글을 삭제하면 댓글 이벤트 리워드 대상에서 제외될 수 있어요.',
      confirmText: '삭제',
      cancelText: '취소',
      onConfirm: () => deleteMutate(reply.id),
    });
  }, [deleteMutate, reply.id]);

  const handleAuthCheck = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, callback: () => void) => {
      e.stopPropagation();
      e.preventDefault();

      if (!isLoggedIn) {
        requestSign();
        return;
      }

      callback();
    },
    [isLoggedIn, requestSign],
  );

  const handleClickLike = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      handleAuthCheck(e, toggleLike);
    },
    [handleAuthCheck, toggleLike],
  );

  const handleClickDelete = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      handleAuthCheck(e, handleDeleteConfirmModal);
    },
    [handleAuthCheck, handleDeleteConfirmModal],
  );

  return (
    <StyledBottom>
      {showLike && (
        <StyledIconButton onClick={handleClickLike}>
          <StyledIconWrapper>
            {liked ? <FavoriteFillIcon color={Color.Primary500} /> : <FavoriteEmptyIcon color={Color.Gray500} />}
          </StyledIconWrapper>
          <StyledFavoriteText>{likeCount > 0 ? likeCount : '좋아요'}</StyledFavoriteText>
        </StyledIconButton>
      )}
      {showDelete && isAuthor && (
        <StyledIconButton onClick={handleClickDelete}>
          <div className="text-xxxs font-medium !text-gray-600 cursor-pointer">삭제하기</div>
        </StyledIconButton>
      )}
    </StyledBottom>
  );
};

const StyledIconButton = styled(IconButton)``;

const StyledBottom = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  margin-top: 16px;
  gap: 16px;
  justify-content: space-between;
`;

const StyledIconWrapper = styled.div`
  width: 16px;
  height: 16px;
  margin-right: 4px;
`;

const StyledFavoriteText = styled.p`
  color: ${Color.Gray500};
  ${Typography.XXXSmall};
`;
